import {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  RoundedBox,
  TableCellType,
  TableSortType,
} from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl, FormattedMessage as T } from "react-intl";
import { useQuery } from "react-query";
import {
  ListRequestOfEnrollmentsFilter,
  EnrollmentsFilter,
  EnrollmentModel,
} from "services/organizationPortalClient";
import { Box, SxProps, Theme, useTheme } from "@mui/material";
import UserCell from "components/UserCell";
import EnrollmentFilter from "./EnrollmentFilter";
import EnrollmentActionButtons from "./EnrollmentActions";
import EnrollmentStatusIcon from "./EnrollmentStatusIcon";
import EnrollmentExpandedInfo from "./EnrollmentExpandedInfo";
import { ExpandableTable } from "@knowfully/ui.common";
import contextStore from "stores/contextStore";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  title: {
    fontSize: "1.2em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    mb: 2,
  },
});

export default function Enrollments() {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { enrollmentService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("enrolledOn");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filter, setFilter] = useState({} as EnrollmentsFilter);
  const { organization } = contextStore.useState();

  const { data, isFetching } = useQuery(
    [
      "enrollments",
      organization?.id,
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
    ],
    () =>
      enrollmentService.getEnrollments(
        ListRequestOfEnrollmentsFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: filter,
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const cells: Array<TableCellType> = [
    {
      accessor: "enrollmentDate",
      type: "date",
      label: t({ id: "enrollmentDate" }),
      customSortAccessor: "enrolledOn",
    },
    {
      accessor: "user",
      type: "node",
      label: t({ id: "user" }),
      View: ({ value }: { value: EnrollmentModel }) => (
        <UserCell
          firstName={value.firstName}
          lastName={value.lastName}
          email={value.email}
        />
      ),
      customSortAccessor: "organizationUser.lastName",
      width: "15%",
    },
    {
      accessor: "title",
      type: "text",
      label: t({ id: "courseTitle" }),
      customSortAccessor: "sku.Title",
      width: "60%",
    },
    {
      accessor: "format",
      type: "node",
      label: t({ id: "format" }),
      noSort: true,
      View: ({ value }: { value: EnrollmentModel }) => (
        <>{value.skuFormat?.plainTextName}</>
      ),
    },
    {
      accessor: "status",
      type: "node",
      label: "",
      customSortAccessor: "enrollmentStatus.value",
      View: EnrollmentStatusIcon,
    },
  ];

  const enrollments = (data?.data ?? []).map(
    (enrollment) =>
      ({
        ...enrollment,
        isExpandable: true,
        renderExpandedData: () => <EnrollmentExpandedInfo value={enrollment} />,
      }) as LazyTableRow
  );

  return (
    <Box>
      <Box sx={styles.title}>
        <T id="enrollments" />
      </Box>
      <RoundedBox>
        <EnrollmentFilter onChange={setFilter} />
      </RoundedBox>
      <RoundedBox>
        <ExpandableTable
          {...{
            "aria-label": t({ id: "enrollments" }),
            rowCount: data?.totalRecords ?? 0,
            rows: enrollments,
            loading: isFetching,
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            titleComponent: (
              <EnrollmentActionButtons
                filter={filter}
                sortDescending={order === "desc"}
                sortField={orderBy}
              />
            ),
            collapsable: true,
          }}
        />
      </RoundedBox>
    </Box>
  );
}
